import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { navLinks } from '../data/mockData';
import DesktopNavItem from './navbar/DesktopNavItem';
import MobileMenu from './navbar/MobileMenu';
import TrustBar from './navbar/TrustBar';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToSection = (href) => {
    const id = href.replace('#', '');
    setIsMobileOpen(false);
    setOpenDropdown(null);

    if (location.pathname !== '/') {
      // Cross-page: navigate home, then scroll after DOM is ready.
      navigate('/' + href);
      // Wait for the new route to render, then scroll (scroll-margin-top handles offset).
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const goToPage = (href) => {
    setIsMobileOpen(false);
    navigate(href);
  };

  return (
    <nav
      className={`sticky top-0 lg:fixed lg:top-0 lg:left-0 lg:right-0 z-50 transition-all duration-500 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-white'
      }`}
    >
      {/* Scrolling trust bar — visible at top, collapses on scroll to reclaim space */}
      <div
        className={`overflow-hidden transition-all duration-500 ease-out ${
          isScrolled ? 'max-h-0 opacity-0' : 'max-h-12 opacity-100'
        }`}
        aria-hidden={isScrolled}
      >
        <TrustBar />
      </div>

      <div className="max-w-[1600px] mx-auto px-6 md:px-10 xl:px-14">
        <div className="flex items-center justify-between h-20 gap-6">
          {/* Logo */}
          <a
            href="/"
            onClick={(e) => { e.preventDefault(); navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className="flex items-center gap-2 group flex-shrink-0"
            data-testid="navbar-logo"
          >
            <img
              src="/imprint-logo-color.svg"
              alt="Imprint M.I.S."
              className="h-12 w-auto object-contain"
            />
          </a>

          {/* Desktop Links */}
          <div className="hidden lg:flex items-center gap-2 xl:gap-3">
            {navLinks.map((link) => (
              <DesktopNavItem
                key={link.label}
                link={link}
                isOpen={openDropdown === link.label}
                onOpen={() => setOpenDropdown(link.label)}
                onClose={() => setOpenDropdown(null)}
                onAnchor={scrollToSection}
                onPage={goToPage}
              />
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:block flex-shrink-0">
            <a
              href="#contact"
              onClick={(e) => { e.preventDefault(); scrollToSection('#contact'); }}
              className="btn-outline text-xs"
              data-testid="navbar-demo-cta"
            >
              Request a Demo
            </a>
          </div>

          {/* Mobile Toggle */}
          <button
            type="button"
            className="lg:hidden p-2 text-gray-700 hover:text-[#00BCE4] transition-colors"
            onClick={() => setIsMobileOpen(!isMobileOpen)}
            data-testid="navbar-mobile-toggle"
            aria-label="Toggle menu"
          >
            {isMobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <MobileMenu
        isOpen={isMobileOpen}
        links={navLinks}
        onAnchor={scrollToSection}
        onPage={goToPage}
        onCtaClick={() => scrollToSection('#contact')}
        onClose={() => setIsMobileOpen(false)}
      />
    </nav>
  );
};

export default Navbar;
